import React, { useEffect, useState } from 'react';

// Shared line-by-line markdown renderer: paragraphs, ##/### headers, * and - lists, **bold**, URLs, tables
export function renderMarkdown(content) {
  if (!content) return null;
  const lines = content.split('\n');
  const elements = [];
  let currentParagraph = [];
  let currentListItems = [];
  let listKey = 0;
  let currentTable = [];
  let tableKey = 0;

  const processTextWithUrls = (text) => {
    const parts = [];
    let lastIndex = 0;
    let keyCounter = 0;
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = [];
    let match;
    while ((match = boldRegex.exec(text)) !== null) {
      matches.push({ index: match.index, end: match.index + match[0].length, type: 'bold', content: match[1], fullMatch: match[0] });
    }
    while ((match = urlRegex.exec(text)) !== null) {
      matches.push({ index: match.index, end: match.index + match[0].length, type: 'url', content: match[0], fullMatch: match[0] });
    }
    matches.sort((a, b) => a.index - b.index);
    const filteredMatches = [];
    for (let i = 0; i < matches.length; i++) {
      const current = matches[i];
      let overlaps = false;
      for (let j = 0; j < filteredMatches.length; j++) {
        const existing = filteredMatches[j];
        if (current.index < existing.end && current.end > existing.index) {
          overlaps = true;
          break;
        }
      }
      if (!overlaps) filteredMatches.push(current);
    }
    filteredMatches.forEach((m) => {
      if (m.index > lastIndex) {
        parts.push({ type: 'text', content: text.substring(lastIndex, m.index), key: `text-${keyCounter++}` });
      }
      parts.push({ type: m.type, content: m.content, key: `${m.type}-${keyCounter++}` });
      lastIndex = m.end;
    });
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex), key: `text-${keyCounter++}` });
    }
    if (parts.length === 0) return text;
    return parts.map((part) => {
      if (part.type === 'bold') return <strong key={part.key}>{part.content}</strong>;
      if (part.type === 'url') {
        return (
          <a key={part.key} href={part.content} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
            {part.content}
          </a>
        );
      }
      return part.content;
    });
  };

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      const paragraphText = currentParagraph.join(' ');
      const processedContent = processTextWithUrls(paragraphText);
      elements.push(<p key={`p-${elements.length}`}>{processedContent}</p>);
      currentParagraph = [];
    }
  };

  const flushList = () => {
    if (currentListItems.length > 0) {
      elements.push(
        <ul key={`ul-${listKey++}`} style={{ marginLeft: '1.5rem', marginTop: '1rem', marginBottom: '1rem' }}>
          {currentListItems.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '0.5rem' }}>{processTextWithUrls(item)}</li>
          ))}
        </ul>
      );
      currentListItems = [];
    }
  };

  const flushTable = () => {
    if (currentTable.length === 0) return;
    const rows = currentTable.filter((row) => row.trim() && row.trim().startsWith('|'));
    if (rows.length >= 2) {
      const headerCells = rows[0].split('|').map((cell) => cell.trim()).filter(Boolean);
      const dataRows = rows.slice(2);
      elements.push(
        <div key={`table-${tableKey++}`} style={{ marginTop: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #dee2e6' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                {headerCells.map((cell, idx) => (
                  <th key={idx} style={{ padding: '0.75rem', border: '1px solid #dee2e6', textAlign: 'left', fontWeight: 'bold' }}>
                    {processTextWithUrls(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataRows.map((row, rowIdx) => {
                const cells = row.split('|').map((cell) => cell.trim()).filter(Boolean);
                return (
                  <tr key={rowIdx}>
                    {cells.map((cell, cellIdx) => (
                      <td key={cellIdx} style={{ padding: '0.75rem', border: '1px solid #dee2e6' }}>
                        {processTextWithUrls(cell)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    currentTable = [];
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    if (!trimmedLine) {
      flushParagraph();
      flushList();
      flushTable();
      return;
    }
    if (trimmedLine.toLowerCase().startsWith('title:')) return;

    if (trimmedLine.startsWith('|')) {
      flushParagraph();
      flushList();
      currentTable.push(trimmedLine);
      return;
    }
    if (currentTable.length > 0) {
      flushTable();
    }

    if (trimmedLine.startsWith('### ')) {
      flushParagraph();
      flushList();
      elements.push(
        <h3 key={`h3-${index}`} style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.25rem', fontWeight: 'bold' }}>
          {processTextWithUrls(trimmedLine.slice(4))}
        </h3>
      );
      return;
    }
    if (trimmedLine.startsWith('## ')) {
      flushParagraph();
      flushList();
      elements.push(
        <h2 key={`h2-${index}`} style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.5rem', fontWeight: 'bold' }}>
          {processTextWithUrls(trimmedLine.slice(3))}
        </h2>
      );
      return;
    }

    if (trimmedLine.startsWith('* ') || trimmedLine.startsWith('- ') || trimmedLine.startsWith('–')) {
      flushParagraph();
      const listItem = trimmedLine.replace(/^[*–-]\s*/, '');
      currentListItems.push(listItem);
      return;
    }

    if (trimmedLine.startsWith('http')) {
      flushParagraph();
      flushList();
      elements.push(
        <p key={`link-${index}`} style={{ marginTop: '1rem' }}>
          <a href={trimmedLine} target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>
            {trimmedLine}
          </a>
        </p>
      );
      return;
    }

    flushList();
    currentParagraph.push(trimmedLine);
  });

  flushParagraph();
  flushList();
  flushTable();
  return <div>{elements}</div>;
}

// Fetches markdown from path (or uses content) and renders with renderMarkdown
export function MarkdownView({ path, content: contentProp }) {
  const [content, setContent] = useState(contentProp ?? '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (contentProp != null) {
      setContent(contentProp);
      setError('');
      return;
    }
    if (!path) {
      setContent('');
      return;
    }
    const url = (process.env.PUBLIC_URL || '') + path;
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        setContent(text);
        setError('');
      })
      .catch((e) => {
        setError('Failed to load document.');
        console.error('Failed to fetch markdown:', e);
      });
  }, [path, contentProp]);

  if (error) return <div className="text-danger small">{error}</div>;
  return renderMarkdown(content);
}
